<template>
  <jade-box>
    <JadeGroupHeader>
      {{ $t('title') }}
      <template #trailing>
        <div class="mt-1">
          <JadeSpinner
            v-if="loading"
            size="large"
          />
        </div>
      </template>
    </JadeGroupHeader>

    <EmptyState v-if="shouldDisplayEmptyState" />
    <template v-if="!loading">
      <div
        v-for="(item, index) in items"
        :key="`list-item-${index}`"
      >
        <router-link
          :key="index"
          :to="toLink(item.transactionId)"
        >
          <jade-list-item
            :label="item.chargeback.reasonDescription"
            :description="item.createdAt"
            navigation
            @onClick="'javascript:void(0)'"
          >
            <template slot="trailing">
              <div class="flex flex-col">
                <jade-text>
                  {{ item.transactionalData.amount }}
                </jade-text>
                <div class="mt-1 items-center flex gap-2">
                  <jade-tooltip
                    v-if="showTooltipDeadline(item.status.code)"
                    placement="top"
                    :text="$t('deadline-expired-tooltip')"
                  >
                    <jade-icon
                      use="circle-info"
                      size="small"
                      color="negative"
                    />
                  </jade-tooltip>
                  <jade-tag
                    size="medium"
                    :variant="statusBadge(item.status.code)"
                  >
                    {{ item.status.label }}
                  </jade-tag>
                </div>
              </div>
            </template>
          </jade-list-item>
        </router-link>
      </div>
    </template>

    <div
      class="mt-2"
      v-if="!shouldDisplayEmptyState"
    >
      <jade-divider />
    </div>
    <div
      class="p-5 flex justify-end"
      v-if="!shouldDisplayEmptyState"
    >
      <jade-pagination
        @onPrevious="requestPrevious"
        @onNext="requestNext"
        :disabledPrevious="!hasPaginationPrevious || loading"
        :disabledNext="!hasPaginationNext || loading"
      />
    </div>
  </jade-box>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  JadeListItem,
  JadeSpinner,
  JadeText,
  JadeGroupHeader,
  JadeTag,
  JadeBox,
  JadeDivider,
  JadePagination,
  JadeTooltip,
  JadeIcon
} from '@jade'
import EmptyState from './EmptyState.vue'

import ChargebackManager from '../chargeback.manager.js'

import { isDisputeStatusFailedToSendValidDocument } from '@enums/chargeback.enum.js'

export default {
  name: 'ChargebackList',
  components: {
    EmptyState,
    JadeListItem,
    JadeGroupHeader,
    JadeTag,
    JadeText,
    JadeBox,
    JadeDivider,
    JadeSpinner,
    JadePagination,
    JadeTooltip,
    JadeIcon
  },
  data () {
    return {
      page: 1,
      limit: 20
    }
  },
  computed: {
    ...mapGetters({
      loading: 'loadingChargebacks',
      chargebacks: 'chargebacks'
    }),
    shouldDisplayEmptyState () {
      return !this.loading && this.isListEmpty
    },
    items () {
      return this.chargebacks?.items
    },
    pagination () {
      return this.chargebacks?.pagination
    },
    hasPaginationPrevious () {
      return this.pagination?.prev
    },
    hasPaginationNext () {
      return this.pagination?.next
    },
    isListEmpty () {
      return !this.items || this.items?.length === 0
    }
  },
  methods: {
    ...mapActions({
      getAllChargebacks: 'GET_ALL_CHARGEBACKS'
    }),
    requestPrevious () {
      this.page = this.page - 1
      this.getAllChargebacks({ page: this.page, limit: this.limit })
    },
    requestNext () {
      this.page = this.page + 1
      this.getAllChargebacks({ page: this.page, limit: this.limit })
    },
    toLink (id) {
      return {
        name: 'chargebackDetails',
        params: {
          chargeback_id: id
        }
      }
    },
    statusBadge (code) {
      return ChargebackManager.chargebackStatusBadge(code)
    },
    showTooltipDeadline (status) {
      return isDisputeStatusFailedToSendValidDocument(status)
    }
  },
  mounted () {
    this.getAllChargebacks()
  }
}
</script>

<i18n>
  {
    "pt-BR": {
      "title": "Lista de contestações", 
      "deadline-expired-tooltip": "Apesar de você ter nos enviado a documentação, ela foi recusada e não existe mais tempo hábil para seguir com a contestação."
    },
    "en-US": {
      "title": "Disputes",
      "deadline-expired-tooltip": "Although you sent us the documentation, it was rejected and there is no more time to pursue the dispute."
    }
  }
</i18n>
